import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { format } from 'date-fns'
import PropTypes from 'prop-types'

import PageSectionHeader from '../../../../../features/components/PageSectionHeader'
import FacebookSocialPosts from './FacebookSocialPosts'
import InstagramSocialPosts from './InstagramSocialPosts'
import CheckNewPostsButton from './CheckNewPostsButton'
import PdfGeneration from '../../../../../features/components/PdfGeneration'

import { insertIf } from '../../../../../helpers/common'
import { getReportPDFSections } from '../getReportPDFSections'

import {
  rightSelector,
  rightSocialPostsSelector,
  rightSocialPostsWasLoadedSelector
} from '../../../../../modules/selectors/rights'

import {
  FACEBOOK_PROVIDER_PLATFORM,
  INSTAGRAM_PROVIDER_PLATFORM
} from '../../../../../constants/selectLists/platformList'

const SocialPostSections = ({ facebookPageData, additionalDataIsLoading, onRefreshButtonClickHandler }) => {
  const [postsInitiallyLoaded, setPostsInitiallyLoaded] = useState(false)

  const socialPosts = useSelector(rightSocialPostsSelector)
  const socialPostsWasLoaded = useSelector(rightSocialPostsWasLoadedSelector)
  const right = useSelector(rightSelector)

  const { name: rightName } = right

  const facebookSocialPosts = useMemo(
    () => socialPosts.filter(post => post.provider === FACEBOOK_PROVIDER_PLATFORM),
    [socialPosts]
  )
  const instagramSocialPosts = useMemo(
    () => socialPosts.filter(post => post.provider === INSTAGRAM_PROVIDER_PLATFORM),
    [socialPosts]
  )
  // todo twitter to be implemented later (don't forget to correct pdf generation inside getReportPDFSections)
  // const twitterSocialPosts = []

  const gatheredSocialPostSections = useMemo(
    () => [
      ...insertIf(facebookSocialPosts.length, {
        title: 'Facebook',
        platform: FACEBOOK_PROVIDER_PLATFORM,
        posts: facebookSocialPosts
      }),
      ...insertIf(instagramSocialPosts.length, {
        title: 'Instagram',
        platform: INSTAGRAM_PROVIDER_PLATFORM,
        posts: instagramSocialPosts
      })
      // ...insertIf(
      //   twitterSocialPosts.length,  {
      //     title: 'Twitter',
      //     platform: '',
      //     posts: twitterSocialPosts
      //   }
      // )
    ],
    [facebookSocialPosts, instagramSocialPosts]
  )

  const firstSocialPostSection = gatheredSocialPostSections[0]

  const renderSocialPostSectionsByPlatform = useCallback(
    platform => {
      switch (platform) {
        case INSTAGRAM_PROVIDER_PLATFORM:
          return <InstagramSocialPosts posts={instagramSocialPosts} facebookPageData={facebookPageData} />
        case FACEBOOK_PROVIDER_PLATFORM:
        default:
          return (
            <FacebookSocialPosts
              posts={facebookSocialPosts}
              facebookPageData={facebookPageData}
              additionalDataIsLoading={additionalDataIsLoading}
            />
          )
      }
    },
    [facebookSocialPosts, instagramSocialPosts, facebookPageData, additionalDataIsLoading]
  )

  useEffect(() => {
    if (!postsInitiallyLoaded && socialPostsWasLoaded) {
      setPostsInitiallyLoaded(true)
    }
  }, [socialPostsWasLoaded, postsInitiallyLoaded])

  return (
    <>
      {/* first section is always visible and performs loading UI and refresh button logic */}
      <section id="facebook-social-posts-list">
        <PageSectionHeader
          title={`Social Posts ${firstSocialPostSection ? ` - ${firstSocialPostSection.title}` : ''}`}
          RightPanel={
            // show button only after initial loading
            postsInitiallyLoaded && (
              <>
                <PdfGeneration
                  title={rightName}
                  getReportPDFSections={getReportPDFSections}
                  fileName={`${rightName} - generated ${format(new Date(), 'dd-MMM-yyyy')}`}
                />
                <CheckNewPostsButton
                  onRefreshButtonClickHandler={onRefreshButtonClickHandler}
                  isLoading={!socialPostsWasLoaded}
                />
              </>
            )
          }
        />
        {renderSocialPostSectionsByPlatform(firstSocialPostSection?.platform)}
      </section>

      {gatheredSocialPostSections.slice(1).map((sectionData, index) => (
        <section key={index} id={`instagram-social-posts-list`}>
          <PageSectionHeader title={`Social Posts - ${sectionData.title}`} />
          {renderSocialPostSectionsByPlatform(sectionData.platform)}
        </section>
      ))}
    </>
  )
}

SocialPostSections.propTypes = {
  facebookPageData: PropTypes.object,
  additionalDataIsLoading: PropTypes.bool,
  onRefreshButtonClickHandler: PropTypes.func
}

export default SocialPostSections
